// src/lib/currency.ts

const ptBRInteger = new Intl.NumberFormat("pt-BR", {
  maximumFractionDigits: 0,
  useGrouping: true,
});

function toExactCents(cents: number | bigint): bigint {
  if (typeof cents === "bigint") return cents;
  if (!Number.isSafeInteger(cents)) {
    throw new RangeError("cents must be a safe integer");
  }
  return BigInt(cents);
}

/**
 * Formats exact integer cents without converting the value to floating-point
 * reais. Dashboard and other integer-cent contracts should prefer this path.
 */
export function formatExactCents(cents: number | bigint): string {
  const exactCents = toExactCents(cents);
  const isNegative = exactCents < 0n;
  const absoluteCents = isNegative ? -exactCents : exactCents;
  const reais = absoluteCents / 100n;
  const centavos = (absoluteCents % 100n).toString().padStart(2, "0");

  return `${isNegative ? "-" : ""}R$\u00a0${ptBRInteger.format(reais)},${centavos}`;
}

/**
 * Formats cents as Brazilian Real while preserving compatibility with legacy
 * callers that still pass fractional calculated cents. Safe integer values use
 * the exact dashboard path above.
 *
 * Example: formatCents(8532050) → "R$ 85.320,50"
 */
export function formatCents(cents: number): string {
  if (Number.isSafeInteger(cents)) return formatExactCents(cents);

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(cents / 100);
}
